import { useEffect, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';

interface ConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  icon: ReactNode;
  isDirty?: boolean;
  children: ReactNode;
  wide?: boolean; // max-w-2xl instead of max-w-lg
}

export function ConfigModal({ isOpen, onClose, title, subtitle, icon, isDirty, children, wide }: ConfigModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);

  const handleClose = () => {
    if (isDirty) {
      if (!window.confirm('Vous avez des modifications non sauvegardées. Quitter quand même ?')) return;
    }
    onClose();
  };

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, isDirty]);

  // Prevent body scroll when open
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
      onClick={(e) => { if (e.target === overlayRef.current) handleClose(); }}
    >
      {/* Modal panel */}
      <div
        className={`
          relative bg-[#14161B] border border-[#2D3139] shadow-2xl flex flex-col
          w-full
          /* Mobile: full screen */
          h-[95dvh] rounded-t-3xl
          /* Desktop: centered dialog */
          sm:h-auto sm:max-h-[90vh] sm:rounded-2xl
          ${wide ? 'sm:max-w-2xl' : 'sm:max-w-lg'}
          overflow-hidden
        `}
        style={{
          animation: 'modalSlideIn 0.25s cubic-bezier(0.22, 1, 0.36, 1)'
        }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-6 pt-6 pb-4 border-b border-[#2D3139] flex-shrink-0">
          {/* Drag handle for mobile */}
          <div className="absolute top-3 left-1/2 -translate-x-1/2 w-10 h-1 rounded-full bg-[#2D3139] sm:hidden" />

          <div className="p-2 rounded-xl bg-indigo-500/20 text-indigo-400 flex-shrink-0">
            {icon}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-white text-lg leading-tight">{title}</h2>
            {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-slate-400 hover:text-white hover:bg-[#2D3139] rounded-xl transition-colors flex-shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto flex-1 custom-scrollbar">
          {children}
        </div>
      </div>

      <style>{`
        @keyframes modalSlideIn {
          from { opacity: 0; transform: translateY(40px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)    scale(1); }
        }
      `}</style>
    </div>
  );
}
